import React, { useState, useEffect } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import SearchBar from "../components/SearchBar";

const PublicGallery = () => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);

  const [searchResults, setSearchResults] = useState([]);
  const [isSearchActive, setIsSearchActive] = useState(false);

  useEffect(() => {
    fetchAllImages();
  }, []);

  const fetchAllImages = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/all-images`);
      console.log("✅ Public Gallery API Response:", response.data);
      setImages(response.data.images);
    } catch (error) {
      console.error("❌ Error fetching images:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (searchParams) => {
    try {
      setLoading(true);
      
      // Only perform search if at least one parameter has a value
      if (!searchParams.query && !searchParams.creator) {
        setIsSearchActive(false);
        setSearchResults([]);
        return;
      }
      
      const params = new URLSearchParams();
      if (searchParams.query) params.append('query', searchParams.query);
      if (searchParams.creator) params.append('creator', searchParams.creator);
      if (searchParams.sortBy) params.append('sortBy', searchParams.sortBy);
      
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/search-images?${params.toString()}`
      );
      
      console.log("Search results:", response.data.images);
      setSearchResults(response.data.images || []);
      setIsSearchActive(true);
    } catch (error) {
      console.error("Error searching images:", error);
      setSearchResults([]);
    } finally {
      setLoading(false);
    }
  };

  const clearSearch = () => {
    setSearchResults([]);
    setIsSearchActive(false);
  };

  const styles = {
    container: {
      padding: '20px',
      maxWidth: '1200px',
      margin: '0 auto'
    },
    title: {
      fontSize: "24px",
      marginBottom: "20px",
      color: "#333",
    },
    grid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(5, 1fr)',
      gap: '15px',
      marginTop: '20px'
    },
    card: {
      border: '1px solid #ddd',
      borderRadius: '8px',
      overflow: 'hidden',
      backgroundColor: 'white',
      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
      transition: 'transform 0.2s',
      cursor: 'pointer'
    },
    imageContainer: {
      width: '100%',
      aspectRatio: '1',
      overflow: 'hidden'
    },
    image: {
      width: '100%',
      height: '100%',
      objectFit: 'cover'
    },
    imageInfo: {
      padding: '10px'
    },
    date: {
      color: '#666',
      fontSize: '0.8em',
      marginBottom: '5px'
    },
    creatorLink: {
      color: "#ff69b4",
      textDecoration: "none",
      fontWeight: "bold",
      display: "block",
      marginBottom: "8px",
      cursor: "pointer",
    },
    prompt: {
      margin: '5px 0',
      fontSize: '0.9em',
      color: '#333'
    },
    modal: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.8)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    },
    modalContent: {
      backgroundColor: 'white',
      padding: '20px',
      borderRadius: '8px',
      maxWidth: '90%',
      maxHeight: '90vh',
      overflow: 'auto',
      position: 'relative'
    },
    closeButton: {
      position: 'absolute',
      right: '10px',
      top: '10px',
      backgroundColor: 'transparent',
      border: 'none',
      fontSize: '24px',
      cursor: 'pointer',
      color: '#333'
    },
    promptCopy: {
      padding: '10px',
      backgroundColor: '#f8f9fa',
      borderRadius: '4px',
      marginTop: '10px',
      cursor: 'pointer',
      fontSize: '0.9em'
    },
    loading: {
      textAlign: 'center',
      padding: '20px',
      fontSize: '1.2rem',
      color: '#666'
    }
  };

  if (loading) {
    return <div style={styles.loading}>Loading gallery...</div>;
  }

  return (
    <div style={styles.container}>
      <div className="welcome-banner" style={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: 'white',
        padding: '20px',
        borderRadius: '8px',
        margin: '0 0 20px 0',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
      }}>
        <h2 style={{ marginTop: '0' }}>Welcome to DreamHotties.com</h2>
        <p>
          Create stunning AI-generated images in just a few clicks. You can register for free and create 
          several anonymous images to start. If you enjoy our service, subscribe for just $10 per month 
          to get 20 monthly credits and purchase additional credits as needed.
        </p>
        <p>
          Share your creations in our Public Gallery or keep them private in your Personal Gallery. 
          Get started today and unleash your creativity!
        </p>
      </div>

      {/* Search components */}
      <SearchBar onSearch={handleSearch} />

      {isSearchActive && (
        <div style={{ marginBottom: '20px' }}>
          <button 
            onClick={clearSearch} 
            style={{
              padding: '8px 16px',
              background: '#f0f0f0',
              border: '1px solid #ddd',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Clear Search Results
          </button>
        </div>
      )}

      <h2 style={styles.title}>
        {isSearchActive 
          ? `Search Results (${searchResults.length})` 
          : 'Public Gallery'}
      </h2>

      <div style={styles.grid}>
        {isSearchActive 
          ? searchResults.map((image, index) => (
              <div 
                key={image.id || index} 
                style={styles.card}
                onClick={() => setSelectedImage(image)}
              >
                <div style={styles.imageContainer}>
                  <img
                    src={image.image_url}
                    alt={image.prompt}
                    style={styles.image}
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = "https://via.placeholder.com/300?text=Image+Not+Found";
                    }}
                  />
                </div>
                <div style={styles.imageInfo}>
                  <span style={styles.date}>
                    {new Date(image.created_at).toLocaleDateString()}
                  </span>
                  <Link 
                    to={`/profile/${image.anonymous_username}`} 
                    style={styles.creatorLink}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {image.anonymous_username}
                  </Link>
                  <p style={styles.prompt}>{image.prompt}</p>
                </div>
              </div>
            ))
          : images.map((image, index) => (
              <div 
                key={image.id || index} 
                style={styles.card}
                onClick={() => setSelectedImage(image)}
              >
                <div style={styles.imageContainer}>
                  <img
                    src={image.image_url}
                    alt={image.prompt}
                    style={styles.image}
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = "https://via.placeholder.com/300?text=Image+Not+Found";
                    }}
                  />
                </div>
                <div style={styles.imageInfo}>
                  <span style={styles.date}>
                    {new Date(image.created_at).toLocaleDateString()}
                  </span>
                  <Link 
                    to={`/profile/${image.anonymous_username}`}
                    style={styles.creatorLink}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {image.anonymous_username}
                  </Link>
                  <p style={styles.prompt}>{image.prompt}</p>
                </div>
              </div>
            ))
        }
      </div>

      {selectedImage && (
        <div style={styles.modal} onClick={() => setSelectedImage(null)}>
          <div style={styles.modalContent} onClick={e => e.stopPropagation()}>
            <button
              style={styles.closeButton}
              onClick={() => setSelectedImage(null)}
            >
              ×
            </button>
            <img
              src={selectedImage.image_url}
              alt={selectedImage.prompt}
              style={{ maxWidth: '100%', maxHeight: '80vh' }}
            />
            
            <div style={{ marginTop: '20px' }}>
              <p><strong>Created:</strong> {new Date(selectedImage.created_at).toLocaleString()}</p>
              <Link 
                to={`/profile/${selectedImage.anonymous_username}`}
                style={styles.creatorLink}
              >
                {selectedImage.anonymous_username}
              </Link>
              <div 
                style={styles.promptCopy} 
                onClick={(e) => {
                  navigator.clipboard.writeText(selectedImage.prompt);
                  e.target.innerHTML = 'Prompt copied!';
                  setTimeout(() => {
                    e.target.innerHTML = selectedImage.prompt;
                  }, 1000);
                }}
              >
                {selectedImage.prompt}
              </div>
            </div>
          </div>
        </div>
      )}

      {images.length === 0 && !loading && !isSearchActive && (
        <p style={{ textAlign: 'center', marginTop: '40px' }}>
          No images found in the gallery.
        </p>
      )}
      
      {searchResults.length === 0 && isSearchActive && (
        <p style={{ textAlign: 'center', marginTop: '40px' }}>
          No images matching your search criteria.
        </p>
      )}
    </div>
  );
};

export default PublicGallery;